// Споделени типове, константи и чисти помощници за работния лист
// „Инвентаризация" (StocktakeWorksheetPage + подкомпонентите в тази папка) —
// същата подредба като при доставка/изписване.
import dayjs from "dayjs";
import { formatUnitPrice } from "../../components/numbers";
import type {
  InterchangeGroup,
  Product,
  Stocktake,
  SubstitutionItem,
} from "../../../../api/inventory";
import type { InRecipesPart } from "./stocktakeRecipes";

// ─── Числови помощници (парсерът приема запетая И точка) ───────────────────
export const parseDecimal = (raw: unknown): number => {
  const value = parseFloat(
    String(raw ?? "")
      .replace(/\s/g, "")
      .replace(",", "."),
  );
  return Number.isNaN(value) ? 0 : value;
};
// „Намерено" приема и ИЗРАЗ от събирания и изваждания: „10+5" е 15, „15-2" е
// 13. Броенето често е на два пъти (рафт + записка) и операторът не бива да
// смята наум. Нищо друго не се тълкува — нито умножение, нито скоби.
//
// Две решения, които изглеждат дребни, но носят поведението:
//  * празните части (докато се пише „10+") се пропускат, за да не скача
//    разликата на 0 между два натиска;
//  * отрицателен резултат се свива до 0 — преброеното количество не може да е
//    отрицателно, а дотук минусът изобщо не се пускаше в полето; „2-5" е
//    видима нула, която операторът поправя, вместо да замине към бекенда.
export const parseQtyExpression = (raw: unknown): number => {
  const terms = String(raw ?? "")
    .replace(/\s/g, "")
    .match(/[+-]?[^+-]+/g);
  if (!terms) return 0;
  const total = terms.reduce((sum, term) => {
    const digits = term.replace(/^[+-]/, "");
    if (digits === "") return sum;
    return term.startsWith("-") ? sum - parseDecimal(digits) : sum + parseDecimal(digits);
  }, 0);
  return total > 0 ? total : 0;
};
// СУМА с 2 знака и ТОЧКА (стойността се ползва и като input). Ед. ЦЕНАТА не
// минава оттук — тя е `formatUnitPrice` (до 4 знака, components/numbers).
export const formatPrice = (value: number) => value.toFixed(2);
// Количество със ЗАПЕТАЯ, точно `decimals` знака (по мерната единица)
export const formatQtyInput = (value: number, decimals = 3) =>
  value.toFixed(decimals).replace(".", ",");
// Знакова стойност за разликите: излишъкът получава „+"
export const formatSigned = (value: number, digits: number) =>
  `${value > 0 ? "+" : ""}${value.toFixed(digits).replace(".", ",")}`;

// ─── Данни на ред (само „докоснатите" продукти живеят в state) ─────────────
// ИМЕНАТА на полетата са част от формата на черновата (v1, `stocktakeDraft.ts`)
// — в localStorage и на сървъра. НЕ ги преименувай.
export type StocktakeRowDraft = {
  counted: string; // намереното (в базови единици), както се пише
  price: string; // ръчна ед. цена (само при излишък), ВИНАГИ нето
  net: string; // стойност на разликата, нето (само при излишък)
  // стойност на разликата, бруто — вече НЯМА поле за нея (само „Стойност" =
  // нето), но стара чернова може да я носи и стойността на реда я уважава
  gross: string;
  batch: string; // партида на новата партида (само при излишък)
  expiry: string; // yyyy-MM-dd, срок на новата партида (само при излишък)
};
export const emptyStocktakeRow = (): StocktakeRowDraft => ({
  counted: "",
  price: "",
  net: "",
  gross: "",
  batch: "",
  expiry: "",
});

// Ред се смята за преброен само когато „Намерено" има стойност (включително 0).
export const isRowCounted = (row: StocktakeRowDraft) =>
  row.counted.trim() !== "";

// Групата на продукта за чиповете над таблицата; "" = без група
export const groupKeyOf = (product: Product) => product.category?.trim() || "";

// ─── Разлика между намереното и търсеното ──────────────────────────────────
export type StocktakeDiffKind = "equal" | "surplus" | "shortage";
// Под този праг разликата е шум от закръгляне, не излишък/липса.
export const DIFF_EPSILON = 0.0005;
export const diffKindOf = (difference: number): StocktakeDiffKind =>
  difference > DIFF_EPSILON
    ? "surplus"
    : difference < -DIFF_EPSILON
      ? "shortage"
      : "equal";

// ─── Един пресметнат ред (продукт + чернова + разлика/стойност) ────────────
// „Raw" полетата са разликата ПРЕДИ взаимозамените — от тях се строят
// предложенията за покриване. Останалите са след прилагането им: тях вижда
// потребителят и точно те се записват.
export type StocktakeComputedRow = {
  product: Product;
  draft: StocktakeRowDraft;
  decimals: number;
  unit: string;
  systemQty: number; // „търсено" — наличността към датата (под 0 се чете като 0)
  // намереното = насипното (полето) + частта в приготвените рецепти; null —
  // насипното не е написано (тогава редът не е преброен, рецепти или не)
  countedQty: number | null;
  looseQty: number | null; // само полето „Намерено" (то отива в заявката)
  inRecipesQty: number; // в приготвените рецепти (0 — няма)
  recipeParts: InRecipesPart[]; // по рецепта — за чипа „+0,630 в рецепти"
  // съставка на рецепта без насипно преброено → „За броене" + пречка
  needsLooseCount: boolean;
  rawDifference: number; // намерено − търсено, БЕЗ приложените замени
  rawKind: StocktakeDiffKind;
  difference: number; // след приложените замени
  kind: StocktakeDiffKind;
  unitPrice: number; // нето, отнася се САМО за разликата
  priceEditable: boolean; // редактируема е само цената на новата партида (излишък)
  priceAuto: boolean; // цената идва от последната доставка, не е писана
  // Излишък без източник на цена: няма последна доставна цена в склада и нищо
  // не е писано → бекендът би заприходил партидата на 0 (новият обект с първа
  // инвентаризация). Ед. цената е ЗАДЪЛЖИТЕЛНА — пречка пред записа.
  priceMissing: boolean;
  differenceNet: number;
  differenceGross: number; // само за реда „Общо" („с ДДС …")
  // Каквото стои в полетата: написаното от потребителя или изведеното от него
  // (стойността на излишъка е нето — колоната „Стойност").
  priceInput: string;
  netInput: string;
};

// ─── Изглед на редовете (решетката и картите) ───────────────────────────────
// Трите блока (`buildStocktakeView` в stocktakeView.ts) — „За броене",
// „Преброени", „Непреброени", всеки с лента-заглавие (`StocktakeBlockBand`).
// Решетката и картите ги рендират като ЕДИН плосък списък (ключ по продукт,
// лентите — „band-{блок}"), не като обвивка по блок: ред, който минава в
// преброените, запазва DOM възела си, иначе фокусираната клетка изчезва (→ към
// цената на току-що преброения ред, Ctrl+Enter към липсващата цена).

// Количество с мярката, за четене: „48 бр", „21,520 кг"
export const formatQtyWithUnit = (value: number, decimals: number, unit: string) =>
  `${formatQtyInput(value, decimals)}${unit ? ` ${unit}` : ""}`;

// Тонът на разликата: излишък зелено, липса червено
export const differenceToneOf = (row: StocktakeComputedRow) =>
  row.kind === "surplus" ? "text-ok-deep" : row.kind === "shortage" ? "text-danger-ink" : "text-muted";

// Текстът на разликата (празно — непреброен, „0" равен, „+1,500" излишък).
// Без тире: непреброеният ред показва само „Търсено" и празното „Намерено".
export const differenceTextOf = (row: StocktakeComputedRow) =>
  !isRowCounted(row.draft)
    ? ""
    : row.kind === "equal"
      ? formatQtyInput(0, row.decimals)
      : formatSigned(row.difference, row.decimals);

// „Ед. цена" и „Стойност" имат смисъл САМО при разлика: при излишък са полета
// (цената и стойността на новата партида), при липса — само за четене (FIFO).
export const hasDifference = (row: StocktakeComputedRow) =>
  isRowCounted(row.draft) && row.kind !== "equal";

// Стойността на излишъка в полето носи знака „+" (колоната е знакова, като
// разликата); знакът не се пише — филтърът на полето го маха при промяна.
export const signedValueInputOf = (row: StocktakeComputedRow) =>
  row.netInput ? `+${row.netInput}` : "";

// ─── Взаимозамени: предложения, покритие и ефектът им върху редовете ───────
// Донор: продукт от същата група, преброен с излишък
export type SubstitutionDonor = {
  productId: number;
  name: string;
  unit: string;
  decimals: number;
  surplusQty: number; // свободният излишък в СВОИТЕ единици
  surplusInRecipientUnits: number; // същото, в единиците на получателя
  coefficient: number; // 1 донор = `coefficient` получател
  sortIndex: number | null;
};

// Една двойка „липса ← излишък", предложена за покриване
export type SubstitutionProposal = {
  recipientProductId: number;
  recipientName: string;
  recipientUnit: string;
  recipientDecimals: number;
  shortageQty: number; // в единиците на получателя (положително число)
  donors: SubstitutionDonor[]; // подредени по приоритет
};

/**
 * Предложенията за покриване: за всяка ЛИПСА търси в групите взаимозаменяеми
 * продукт, преброен с ИЗЛИШЪК. Покрива се min(липса, излишък на донора).
 * Гледа РАЗЛИКИТЕ ПРЕДИ замените (`rawDifference`/`rawKind`) — иначе двойката
 * би изчезнала в мига, в който я приложиш, и не би могла да се отмени.
 */
export const buildSubstitutionProposals = (
  countedRows: StocktakeComputedRow[],
  groups: InterchangeGroup[],
): SubstitutionProposal[] => {
  const rowByProduct = new Map(countedRows.map((row) => [row.product.id, row]));
  const proposals: SubstitutionProposal[] = [];
  for (const row of countedRows) {
    if (row.rawKind !== "shortage") continue;
    const shortageQty = -row.rawDifference;
    const donors = new Map<number, SubstitutionDonor>();
    for (const group of groups) {
      const recipientMember = group.members.find(
        (m) => m.productId === row.product.id,
      );
      if (!recipientMember) continue;
      const recipientCoefficient = recipientMember.coefficient || 1;
      for (const member of group.members) {
        if (member.productId === row.product.id) continue;
        if (donors.has(member.productId)) continue;
        const donorRow = rowByProduct.get(member.productId);
        // Донор може да е САМО продукт, преброен с ИЗЛИШЪК.
        if (!donorRow || donorRow.rawKind !== "surplus") continue;
        const donorCoefficient = member.coefficient || 1;
        const surplusQty = donorRow.rawDifference;
        const coefficient = donorCoefficient / recipientCoefficient;
        donors.set(member.productId, {
          productId: member.productId,
          name: member.productName,
          unit: donorRow.unit,
          decimals: donorRow.decimals,
          surplusQty,
          surplusInRecipientUnits: surplusQty * coefficient,
          coefficient,
          sortIndex: member.sortIndex,
        });
      }
    }
    if (donors.size === 0) continue;
    proposals.push({
      recipientProductId: row.product.id,
      recipientName: row.product.name,
      recipientUnit: row.unit,
      recipientDecimals: row.decimals,
      shortageQty,
      donors: [...donors.values()].sort(
        (a, b) => (a.sortIndex ?? Infinity) - (b.sortIndex ?? Infinity),
      ),
    });
  }
  return proposals;
};

// Колко се покрива от избрания донор (в единиците на получателя), при
// наличен излишък `availableDonorQty` (в единиците на ДОНОРА)
const coveredQtyOf = (
  proposal: SubstitutionProposal,
  donor: SubstitutionDonor | undefined,
  availableDonorQty: number,
): number =>
  donor
    ? Math.min(proposal.shortageQty, availableDonorQty * donor.coefficient)
    : 0;

/**
 * ЕДИН донор може да покрие няколко липси, но само до излишъка си: 4 кг излишък
 * не стигат за две липси по 3 кг. Затова приложените двойки се изминават по ред
 * и всяка ИЗЯЖДА от излишъка на донора си.
 *
 * Връща: колко реално покрива всяка приложена двойка (в единици на получателя)
 * и колко излишък ОСТАВА на всеки донор (в неговите единици) — оттам панелът
 * знае кои още неприложени двойки вече не са възможни (disabled).
 */
export const substitutionAvailability = (
  proposals: SubstitutionProposal[],
  appliedDonorByRecipient: Map<number, number>,
): {
  coveredByRecipient: Map<number, number>;
  donorRemaining: Map<number, number>;
} => {
  const donorRemaining = new Map<number, number>();
  for (const proposal of proposals) {
    for (const donor of proposal.donors) {
      if (!donorRemaining.has(donor.productId)) {
        donorRemaining.set(donor.productId, donor.surplusQty);
      }
    }
  }
  const coveredByRecipient = new Map<number, number>();
  for (const proposal of proposals) {
    const donor = appliedDonorOf(proposal, appliedDonorByRecipient);
    if (!donor) continue;
    const available = donorRemaining.get(donor.productId) ?? 0;
    const covered = coveredQtyOf(proposal, donor, available);
    if (covered <= DIFF_EPSILON) continue;
    coveredByRecipient.set(proposal.recipientProductId, covered);
    donorRemaining.set(
      donor.productId,
      available - covered / donor.coefficient,
    );
  }
  return { coveredByRecipient, donorRemaining };
};

// Донорът по избор (или предложеният по подразбиране) — за падащото меню
export const donorOf = (
  proposal: SubstitutionProposal,
  productId: number | undefined,
): SubstitutionDonor | undefined =>
  proposal.donors.find((d) => d.productId === productId) ?? proposal.donors[0];

// Донорът, с който двойката е ПРИЛОЖЕНА. Няма fallback: ако преброеното се
// промени и старият донор вече не е излишък, двойката спира да важи —
// еднакво за изгледа, за нетирането на редовете и за payload-а.
export const appliedDonorOf = (
  proposal: SubstitutionProposal,
  appliedDonorByRecipient: Map<number, number>,
): SubstitutionDonor | undefined =>
  proposal.donors.find(
    (d) =>
      d.productId === appliedDonorByRecipient.get(proposal.recipientProductId),
  );

/**
 * Ефектът на ПРИЛОЖЕНИТЕ замени върху разликите: продукт → колко се прибавя към
 * `rawDifference`. Получателят си връща покритото (липсата намалява), донорът
 * дава своя еквивалент (излишъкът намалява) — точно каквото ще запише и
 * бекендът, затова таблицата показва резултата ОЩЕ при натискането на „Замени".
 */
export const substitutionAdjustments = (
  proposals: SubstitutionProposal[],
  appliedDonorByRecipient: Map<number, number>,
): Map<number, number> => {
  const { coveredByRecipient } = substitutionAvailability(
    proposals,
    appliedDonorByRecipient,
  );
  const adjustments = new Map<number, number>();
  const add = (productId: number, value: number) =>
    adjustments.set(productId, (adjustments.get(productId) ?? 0) + value);
  for (const proposal of proposals) {
    const donor = appliedDonorOf(proposal, appliedDonorByRecipient);
    const covered = coveredByRecipient.get(proposal.recipientProductId) ?? 0;
    if (!donor || covered <= DIFF_EPSILON) continue;
    add(proposal.recipientProductId, covered);
    add(donor.productId, -covered / donor.coefficient);
  }
  return adjustments;
};

// Приложените двойки → payload за бекенда (quantity е в единици на получателя)
export const toSubstitutionItems = (
  proposals: SubstitutionProposal[],
  appliedDonorByRecipient: Map<number, number>,
): SubstitutionItem[] => {
  const { coveredByRecipient } = substitutionAvailability(
    proposals,
    appliedDonorByRecipient,
  );
  return proposals
    .map((proposal) => {
      const donor = appliedDonorOf(proposal, appliedDonorByRecipient);
      return {
        recipientProductId: proposal.recipientProductId,
        donorProductId: donor?.productId ?? 0,
        quantity: coveredByRecipient.get(proposal.recipientProductId) ?? 0,
      };
    })
    .filter((item) => item.donorProductId > 0 && item.quantity > DIFF_EPSILON);
};

// Черновата (форматът, localStorage и сървърът) е в `stocktakeDraft.ts`.

// ─── Зареждане на записана инвентаризация в редове (редакция) ──────────────
export const rowsOfStocktake = (
  stocktake: Stocktake,
  decimalsOf: (productId: number) => number,
): Record<number, StocktakeRowDraft> => {
  const out: Record<number, StocktakeRowDraft> = {};
  for (const line of stocktake.lines) {
    if (line.counted == null) continue;
    const row = emptyStocktakeRow();
    // В полето е НАСИПНОТО: записаното преброено минус частта в приготвените
    // рецепти (тя се връща от рецептите на документа — stocktakeRecipes.ts)
    const loose = Math.max(0, line.counted - (line.inRecipes ?? 0));
    row.counted = formatQtyInput(loose, decimalsOf(line.productId));
    // ръчната цена — с ЦЕНОВАТА точност (до 4 знака), не като сума
    if (line.priceManual) row.price = formatUnitPrice(line.unitPrice);
    row.batch = line.batchNumber ?? "";
    // UTC ISO → ЛОКАЛНА дата, иначе срокът се мести с ден назад при редакция
    row.expiry = line.expiryDate
      ? dayjs(line.expiryDate).format("YYYY-MM-DD")
      : "";
    out[line.productId] = row;
  }
  return out;
};

// Записаната инвентаризация → състоянието на листа при редакция (без
// рецептите — те са `useStocktakeRecipes().loadFromStocktake`)
export const sheetOfStocktake = (
  stocktake: Stocktake,
  decimalsOf: (productId: number) => number,
) => ({
  // UTC ISO → ЛОКАЛНА дата: документ от 01:00 иначе излиза с вчерашна дата
  date: dayjs(stocktake.date || undefined).format("YYYY-MM-DD"),
  warehouseId: stocktake.warehouseId,
  reason: stocktake.reason ?? "",
  // запазеното „търсено" (очакваното ПРЕДИ инвентаризацията)
  savedSystemByProduct: new Map(
    stocktake.lines
      .filter((line) => line.systemQty != null)
      .map((line) => [line.productId, line.systemQty as number]),
  ),
  productIds: stocktake.lines.map((line) => line.productId),
  rows: rowsOfStocktake(stocktake, decimalsOf),
  appliedDonorByRecipient: new Map(
    (stocktake.substitutions ?? []).map((s) => [s.recipientProductId, s.donorProductId]),
  ),
});

// ─── Оцветяване на съвпадналите думи в резултат на търсачката ───────────────
// Оцветяването на съвпаденията е ОБЩО за модула (един файл, три търсачки).
export { highlightWords } from "../../components/highlight";

// ─── Решетка / клетки ───────────────────────────────────────────────────────
// grid-template-columns: # · продукт · търсено · намерено · разлика ·
// ед. цена · стойност. Мярката е В числата („48 бр"), не в своя колона;
// стойността е ЕДНА — нетото на разликата (брутото е само в реда „Общо").
export const LINE_GRID_CLASS =
  "grid grid-cols-[44px_minmax(220px,1.8fr)_130px_150px_110px_110px_130px]";
export const LINE_GRID_MIN_WIDTH_CLASS = "min-w-[900px]";
// колоните на LINE_GRID_CLASS — за пълнежа над реда „Общо"
export const LINE_GRID_COLUMN_COUNT = 7;

// колони на падащия списък на търсачката (име · наличност · ед. цена)
export const DROPDOWN_GRID_CLASS = "grid grid-cols-[minmax(0,1fr)_200px_130px]";
// … и на телефон — без ед. цена
export const DROPDOWN_GRID_NARROW_CLASS = "grid grid-cols-[minmax(0,1fr)_minmax(90px,auto)]";

// Плоска клетка — същият вид като в работния лист „Доставка": прозрачна
// рамка/фон, на фокус оранжева рамка + бял фон + ring. leading-[normal] =
// line-height като от `font:` shorthand-а (иначе клетките стават по-високи).
// Червена рамка при празно задължително поле. Носи и width/style, защото се
// слага и върху borderless antd контрол (там иначе няма какво да се оцвети).
export const FLAT_CELL_ERROR_CLASS =
  "!border-[1.5px] !border-solid !border-danger";
const FLAT_CELL_BASE_CLASS =
  "box-border w-full rounded-[5px] border-[1.5px] leading-[normal] outline-none " +
  "focus:border-brand focus:bg-surface focus:not-italic focus:shadow-[0_0_0_3px_rgba(217,127,0,0.15)]";
export const FLAT_CELL_CLASS = `${FLAT_CELL_BASE_CLASS} border-transparent bg-transparent`;
// Празното „Намерено" (без тире вътре): тънка рамка казва „тук се пише", иначе
// непреброеният ред изглежда като само за четене
export const EMPTY_ENTRY_CELL_CLASS = `${FLAT_CELL_BASE_CLASS} border-shade-3 bg-surface`;

// Навигируеми колони: 0 намерено · 1 ед. цена · 2 стойност (нето на разликата).
// Ед. цена и Стойност са полета САМО при излишък — ←/→ прескачат липсващата
// клетка, а ↑/↓ към ред без нея падат наляво до „Намерено" (виж focusCell).
// Ключовете са имената на полетата в черновата (`net` = колоната „Стойност").
export const STOCKTAKE_COLS = { counted: 0, price: 1, net: 2 } as const;
export const COL_COUNT = 3;
// id на клетка — по ПРОДУКТ, не по индекс на реда: секциите се пренареждат
// (преброеното отива най-горе), а идентичността на клетката трябва да остане.
export const cellId = (productId: number, col: number) =>
  `stx-${productId}-${col}`;
// Полето „Партида" на новата партида — извън решетката (без `data-c`), затова
// с друго id; връзката „+ партида / срок" го фокусира след отварянето
export const batchFieldId = (productId: number) => `stx-batch-${productId}`;
